import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {NucleotideType} from 'biojs/BioConstants';
import * as CrisprConstants from 'constants/CRISPRConstants';
import {CRISPRAnalysisSequenceAttributes, CRISPRGuide, OffTargetSite} from 'constants/CRISPRConstants';
import {CRISPRAnalysis} from 'constants/CRISPRConstants';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import DoenchScore from 'molbio/crispr/doenchScore';
import GuideGroupTable, {
  GuideGroupTableColumnSpec,
  GuideGroupTableHelpOptions,
} from 'molbio/crispr/GuideGroupTable';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import {capitalize} from 'util/NameUtil';
import pick from 'util/pick';
import {Optional} from 'util/Types';

import {OffTargetScore} from './BaseEditGuideGroupTable';
import CRISPRAnalysisActions from './CRISPRAnalysisActions';
import CRISPRAnalysisStore from './CRISPRAnalysisStore';
import * as CRISPRUtil from './CRISPRUtil';
import OffTargetModal from './OffTargetModal';

function OnTargetScore({
  useAzimuthOnTargetScore,
  onTargetWarning,
}: {
  useAzimuthOnTargetScore: boolean;
  onTargetWarning?: JSX.Element;
}): JSX.Element {
  if (useAzimuthOnTargetScore) {
    return (
      <div>
        Optimized score from{' '}
        <a href={URLS.CFD_SCORE_PAPER} target="_blank" rel="noopener noreferrer">
          Doench, Fusi et al. (2016)
        </a>
        . This score is optimized for 20bp guides with NGG PAMs.{onTargetWarning}
        <br />
        <br />
        Score is from 0-100. Higher is better.
      </div>
    );
  } else {
    return (
      <div>
        <a href={URLS.DOENCH_ACTIVITY} target="_blank" rel="noopener noreferrer">
          Activity score
        </a>{' '}
        from Doench et al. (2014). Requires 20 bp guides and is only available for Cas9.
        <br />
        <br />
        Score is from 0-100. Higher is better.
      </div>
    );
  }
}

const {URLS, MAX_OFF_TARGET_GUIDE_LENGTH, RECOMMENDED_AZIMUTH_ON_TARGET_GUIDE_LENGTH, SORT_KEY} =
  CrisprConstants;
const {DOENCH_GUIDE_SIZE} = DoenchScore;

export interface CRISPRGuideGroupTableProps {
  allowEdits: boolean;
  analysis: CRISPRAnalysis;
  guideGroups: Array<Array<CRISPRGuide>>;
  guideGroupSize: 1 | 2;
  openAssemblyTab: (analysis: unknown, guides: Array<CRISPRGuide>) => void;
  saveGuidePrimers: (folderId: string, nucleotideType: NucleotideType, tagSchemaId: string | null) => void;
  showGuidePrimers: (guides: Array<CRISPRGuide>) => void;
  sequencePrimers: Array<{start: number; end: number; strand: StrandOfDoubleStranded}>;
  sequence: CRISPRAnalysisSequenceAttributes;
  selection: {start: number; end: number | null};
  setSelection: (start: number, end: number | null) => void;
  guideGroupSort: CrisprConstants.GuideGroupSort;
  selectedGuideGroups: {[guideGroupKey: string]: boolean};
}

interface State {
  offTargetModalGuide: null | CRISPRGuide;
}

class CRISPRGuideGroupTable extends React.Component<CRISPRGuideGroupTableProps, State> {
  override state: State = {
    offTargetModalGuide: null,
  };

  getGuideSequenceColumnSpec = (): GuideGroupTableColumnSpec => ({
    header: 'Guide sequence',
    help: {
      title: 'Guide sequence help',
      content:
        'Guide sequences are shown as DNA. When you save guides as oligos, you can elect to save your guides as DNA or RNA.',
    },
  });

  getOnTargetColumnSpec = (): GuideGroupTableColumnSpec => {
    if (
      CRISPRUtil.isCas9Pam(this.props.analysis.pam) &&
      this.props.analysis.guideLength >= DOENCH_GUIDE_SIZE
    ) {
      return {
        header: 'On\u2011Target',
        extraHeader: 'score',
        sortKey: SORT_KEY.EFFICIENCY,
        help: this.renderOnTargetHelp(),
      };
    }
    return {
      header: 'On\u2011target',
      extraHeader: 'score',
      sortKey: SORT_KEY.EFFICIENCY,
      help: {
        title: 'On-target score is disabled',
        content:
          'On-target scores are only available for Cas9 PAMs and 20 bp or longer guides. Please ' +
          'change your analysis settings to see these scores.',
        helpClassName: 'text-danger',
      },
    };
  };

  getShouldDisplayTier = (): boolean =>
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    this.props.analysis.regions.some((r: CrisprConstants.CRISPRRegion) => r.crisprIaTier !== null);

  getColumnSpec = (): Array<GuideGroupTableColumnSpec> => {
    let offTargetWarning;
    // Deliberately prefer showing Cpf1 message if both cases are true
    if (this.props.analysis.isLeftPam) {
      offTargetWarning = (
        <>
          For Cpf1, <b>we do not apply positional weights</b>. More data on Cpf1 is needed to understand how
          it differs from Cas9.
        </>
      );
    } else if (this.props.analysis.guideLength > MAX_OFF_TARGET_GUIDE_LENGTH) {
      offTargetWarning =
        'The off-target analysis only takes into account the ' +
        `${MAX_OFF_TARGET_GUIDE_LENGTH} bp closest to PAM.`;
    }

    const offTargetHelp: GuideGroupTableHelpOptions = {
      title: 'Off-target score',
      content: (
        <OffTargetScore type={this.props.analysis.offTargetScoreType} offTargetWarning={offTargetWarning} />
      ),
      helpClassName: classNames({
        'text-danger':
          this.props.analysis.guideLength > MAX_OFF_TARGET_GUIDE_LENGTH || this.props.analysis.isLeftPam,
      }),
    };

    let columns: Array<GuideGroupTableColumnSpec> = [
      {
        sortKey: SORT_KEY.POSITION,
        header: 'Cut position',
      },
      {
        header: 'Strand',
      },
      this.getGuideSequenceColumnSpec(),
      {
        header: 'PAM',
      },
      this.getOnTargetColumnSpec(),
    ];
    if (this.props.guideGroupSize === 1) {
      columns = columns.concat([
        {
          sortKey: SORT_KEY.SPECIFICITY_SINGLE,
          header: 'Off\u2011target',
          extraHeader: 'score',
          help: offTargetHelp,
        },
      ]);
    } else {
      columns = columns.concat([
        {
          sortKey: SORT_KEY.SPECIFICITY_PAIR,
          header: 'Off\u2011target',
          extraHeader: '(pair)',
          help: offTargetHelp,
        },
        {
          sortKey: SORT_KEY.SPECIFICITY_SINGLE,
          header: 'Off\u2011target',
          extraHeader: '(single)',
          help: offTargetHelp,
        },
      ]);
    }
    if (this.getShouldDisplayTier()) {
      columns = columns.concat([
        {
          sortKey: SORT_KEY.CRISPR_IA_TIER,
          header: 'Tier',
        },
      ]);
    }
    return columns;
  };

  getGuideGroupComponentsMap = (): {[key: string]: Array<Array<JSX.Element>>} => {
    const guideGroupsComponentMap: {[key: string]: Array<Array<JSX.Element>>} = {};
    this.props.guideGroups.forEach((guideGroup) => {
      guideGroupsComponentMap[CRISPRAnalysisStore.guideGroupKey(guideGroup)] = guideGroup.map((guide, i) => {
        const rowElements = [
          <td className="text-left" key="position">
            {sequtil.modelStartToDisplay(this.getGuidePosition(guide), this.props.sequence.startIndex)}
          </td>,
          <td className="mono" key="strand">
            {CRISPRUtil.renderStrand(guide)}
          </td>,
          <td className="mono" key="bases">
            {guide.bases}
          </td>,
          <td className="mono" key="pam">
            {guide.pam}
          </td>,
          <td
            className="text-left"
            key="onTargetScore"
            {...getTestProps({component: 'CRISPRGuideGroupTable', element: 'onTargetScore'})}
          >
            {this.renderDoenchScore(guide)}
          </td>,
        ];
        if (i === 0) {
          const groupOffTargetScore = this.groupOffTargetScore(guideGroup);
          const groupOffTargetScoreString = groupOffTargetScore ? groupOffTargetScore.toFixed(1) : null;
          const shouldLinkModal = this.props.guideGroupSize === 1;
          rowElements.push(
            <td className="text-left" rowSpan={this.props.guideGroupSize} key="groupOffTargetScore">
              {this.renderOffTargetScore(guide, groupOffTargetScoreString, shouldLinkModal)}
            </td>
          );
        }
        if (this.props.guideGroupSize > 1) {
          const offTargetScore = this.offTargetScore(guide);
          const offTargetScoreString = offTargetScore ? offTargetScore.toFixed(1) : null;
          const shouldLinkModal = true;
          rowElements.push(
            <td className="text-left" key="offTargetScore">
              {this.renderOffTargetScore(guide, offTargetScoreString, shouldLinkModal)}
            </td>
          );
        }
        if (this.getShouldDisplayTier()) {
          const tier = this.crisprIaTier(guideGroup);
          const tierText = tier ? `Tier ${tier}` : '-';
          rowElements.push(
            <td className="text-left" key="crisprIaTier">
              {tierText}
            </td>
          );
        }
        return rowElements;
      });
    });
    return guideGroupsComponentMap;
  };

  getGuidePosition = (guide: CRISPRGuide): number => guide.position;

  getSortIteratee = (): ((guideGroup: Array<CRISPRGuide>) => number | null) => {
    const sortKey = this.props.guideGroupSort.key;
    switch (sortKey) {
      case SORT_KEY.POSITION:
        return this.groupPosition;
      case SORT_KEY.SPECIFICITY_PAIR:
        return this.groupOffTargetScore;
      case SORT_KEY.SPECIFICITY_SINGLE:
        return this.minOffTargetScore;
      case SORT_KEY.EFFICIENCY:
        return this.groupDoenchScore;
      case SORT_KEY.CRISPR_IA_TIER:
        return this.crisprIaTier;
      default:
        throw new Error(`Unrecognized key in guide group sorting function: ${sortKey}`);
    }
  };

  doenchScore = (guide: CRISPRGuide): Optional<number> =>
    !this.props.analysis.isLeftPam ? guide.onTargetScore : null;

  groupDoenchScore = (guideGroup: Array<CRISPRGuide>): number =>
    Math.min(...guideGroup.map((guide: CRISPRGuide) => this.doenchScore(guide)!));

  groupOffTargetScore = (guideGroup: Array<CRISPRGuide>): number | null => {
    if (this.hasNullOffTargetScore(guideGroup)) {
      return null;
    }
    let result = 100;
    for (let j = 0; j < guideGroup.length; j++) {
      result *= this.offTargetScore(guideGroup[j])! / 100;
    }
    return result;
  };

  groupPosition = (guideGroup: Array<CRISPRGuide>): number =>
    Math.min(...guideGroup.map((guide: CRISPRGuide) => this.getGuidePosition(guide)));

  crisprIaTier = (guideGroup: Array<CRISPRGuide>): number | null => {
    const [region] = this.props.analysis.regions.filter(
      (r: CrisprConstants.CRISPRRegion) => r.id === guideGroup[0].regionId
    );
    return region.crisprIaTier;
  };

  guideGroupSelectionRange = (guideGroup: Array<CRISPRGuide>): {start: number; end: number} => {
    if (guideGroup.length === 1) {
      return pick(this.guideToPrimer(guideGroup[0]), 'start', 'end');
    }
    assert(guideGroup.length === 2, 'expected guideGroup to have length 2');

    const sortedGuides = _.min([guideGroup, [guideGroup[1], guideGroup[0]]], ([g1, g2]) => {
      const start = CRISPRUtil.guideStart(g1, this.props.sequence.length, this.props.analysis.isLeftPam);

      // If the sequence is linear, ensure the earlier guide start is first
      if (!this.props.sequence.circular) {
        return start;
      }

      const end = CRISPRUtil.guideEnd(g2, this.props.sequence.length, this.props.analysis.isLeftPam);
      // If the sequence is circular, the selection could wrap around the start of the sequence
      // We want to make sure that the selection covers both guides so we want to take the larger of the two
      // selections.
      return -1 * sequtil.distance(start, end, this.props.sequence.length, true);
    });

    assert(
      Boolean(sortedGuides[1] && sortedGuides[0]),
      'Forward or reverse guide is undefined in paired guide table',
      {guideGroup, sortedGuides}
    );
    return {
      start: CRISPRUtil.guideStart(
        sortedGuides[0],
        this.props.sequence.length,
        this.props.analysis.isLeftPam
      ),
      end: CRISPRUtil.guideEnd(sortedGuides[1], this.props.sequence.length, this.props.analysis.isLeftPam),
    };
  };

  // Dead code
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  guideGroupClickHandler = (guideGroup: Array<CRISPRGuide>, e: any): void => {
    const {start, end} = this.guideGroupSelectionRange(guideGroup);
    this.props.setSelection(start, end);
  };

  guideToPrimer = (guide: CRISPRGuide): {start: number; end: number; strand: StrandOfDoubleStranded} => ({
    start: CRISPRUtil.guideStart(guide, this.props.sequence.length, this.props.analysis.isLeftPam),
    end: CRISPRUtil.guideEnd(guide, this.props.sequence.length, this.props.analysis.isLeftPam),
    strand: guide.strand!,
  });

  guidesToTSV = (guides: Array<CRISPRGuide>): string => {
    const tsvHeaderRows = ['Position\tStrand\tSequence\tPAM\tSpecificity score\tEfficiency score'];
    const tsvRows = guides.map((guide: CRISPRGuide) => {
      const position = sequtil.modelStartToDisplay(guide.position, this.props.sequence.startIndex);
      const row = [
        position,
        guide.strand,
        guide.bases,
        guide.pam,
        this.offTargetScore(guide),
        this.doenchScore(guide),
      ];
      return row.join('\t');
    });
    return tsvHeaderRows.concat(tsvRows).join('\n');
  };

  hasNullOffTargetScore = (guideGroup: Array<CRISPRGuide>): boolean =>
    guideGroup.some((guide: CRISPRGuide) => !this.offTargetScore(guide));

  isGuideGroupSelected = (guideGroup: Array<CRISPRGuide>): boolean =>
    CRISPRAnalysisStore.isGuideGroupSelected(this.props.selectedGuideGroups, guideGroup);

  minOffTargetScore = (guideGroup: Array<CRISPRGuide>): number | null => {
    if (this.hasNullOffTargetScore(guideGroup)) {
      return null;
    }
    return Math.min(...guideGroup.map((guide: CRISPRGuide) => this.offTargetScore(guide)!));
  };

  // Dead code
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notifyGuidesTSVCopied = (which: any): void => {
    notifications.success(
      `${capitalize(which)} guides have been copied in tab-delimited format,
      which can be pasted into Excel.`,
      {duration: 6, id: `${which}-guides-copied`}
    );
  };

  offTargetScore = (guide: CRISPRGuide): number | null => guide.offTargetScore;

  // Dead code
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectGuideGroups = (guideGroups: Array<Array<CRISPRGuide>>, e: any): void => {
    CRISPRAnalysisActions.toggleSelectedGuideGroups(this.props.analysis.id, guideGroups, e.target.checked);
  };

  showOffTargetModal = (guide: CRISPRGuide): void => {
    this.setState({offTargetModalGuide: guide});
  };

  // Dead code
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortGuideGroups = (sortKey: any): void => {
    if (this.props.guideGroupSort.key === sortKey) {
      CRISPRAnalysisActions.updateGuideGroupSort(this.props.analysis.id, {
        reverse: !this.props.guideGroupSort.reverse,
      });
    } else {
      CRISPRAnalysisActions.updateGuideGroupSort(this.props.analysis.id, {
        key: sortKey,
        reverse:
          [SORT_KEY.SPECIFICITY_SINGLE, SORT_KEY.SPECIFICITY_PAIR, SORT_KEY.EFFICIENCY].indexOf(sortKey) !==
          -1,
      });
    }
  };

  renderDoenchScore = (guide: CRISPRGuide): string | null => {
    const score = this.doenchScore(guide);
    if (!score) {
      return null;
    }
    if (this.props.analysis.useAzimuthOnTargetScore && !guide.usedFullModel) {
      return `${score.toFixed(1)}*`;
    }
    return score.toFixed(1);
  };

  renderOffTargetScore = (
    guide: CRISPRGuide,
    score: string | null,
    linkModal: boolean
  ): string | JSX.Element | null => {
    if (!linkModal) {
      return score;
    }
    return (
      <a
        onClick={() => {
          this.showOffTargetModal(guide);
        }}
        {...getTestProps({component: 'CRISPRGuideGroupTable', element: 'offTargetScore'})}
      >
        {score}
      </a>
    );
  };

  renderOnTargetHelp = (): GuideGroupTableHelpOptions => {
    if (this.props.analysis.useAzimuthOnTargetScore) {
      let onTargetWarning;
      if (this.props.analysis.guideLength !== RECOMMENDED_AZIMUTH_ON_TARGET_GUIDE_LENGTH) {
        onTargetWarning = (
          <span>
            <b>Warning:</b> the on-target model <b>does not</b> expect non-20bp guides. Results may be
            inaccurate.
          </span>
        );
      }
      return {
        title: 'On-target score',
        content: <OnTargetScore useAzimuthOnTargetScore={true} onTargetWarning={onTargetWarning} />,
      };
    }
    return {
      title: 'On-target score',
      content: <OnTargetScore useAzimuthOnTargetScore={false} />,
    };
  };

  override render(): JSX.Element {
    // eslint-disable-next-line no-restricted-syntax
    const guideGroupTableProps = _.extend({}, this.props, {
      guideGroupComponentsMap: this.getGuideGroupComponentsMap(),
      columns: this.getColumnSpec(),
      tableClassName: 'crispr-guidesTable',
      getGuidePosition: this.getGuidePosition,
      getSortIteratee: this.getSortIteratee,
      guidesToTSV: this.guidesToTSV,
    });

    return (
      <div>
        {(() => {
          if (this.state.offTargetModalGuide) {
            return (
              <OffTargetModal
                analysis={this.props.analysis}
                // Note that some CRISPR components expect `sites` to be non-null. We should probably have
                // versions of `CRISPRGuide` with both stricter and looser types to be more accurate. Currently
                // it's unclear to me exactly at what point the type can be constrained.
                guide={this.state.offTargetModalGuide as CRISPRGuide & {sites: Array<OffTargetSite>}}
                onRequestClose={() => {
                  this.setState({offTargetModalGuide: null});
                }}
                region={_.findWhere(this.props.analysis.regions, {
                  id: this.state.offTargetModalGuide.regionId,
                })!}
              />
            );
          }
          return null;
        })()}
        <div {...getTestProps({component: 'CRISPRGuideGroupTable', element: 'root'})}>
          <GuideGroupTable {...guideGroupTableProps} />
        </div>
      </div>
    );
  }
}

export default CRISPRGuideGroupTable;
